import { Skeleton, Stack, useTheme } from "@mui/material";
import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Rectangle,
  type RectangleProps,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  barChartColor,
  type ChartProps,
  ChartTooltip,
  LegendBuilder,
  TickLabel,
} from "../Utils";

/** One chart row — the stacked values of a single bar, keyed `element_<index>`. */
type StackRow = Record<string, string | number | undefined>;

interface StackedBarChartProps extends ChartProps {
  /** Rounds the outer corners of the last visible segment of every stack. */
  barRadius?: number;
  /** Fixed bar thickness; bars size themselves to the category width without it. */
  barSize?: number;
}

export const StackedBarChart = ({
  data = [],
  labelKey = "label",
  valueKey = "value",
  width,
  height,
  customTooltip,
  isLoading = false,
  showLegends = true,
  showAxisLines = false,
  showGrid = false,
  onItemClick,
  layout = "horizontal",
  barRadius = 0,
  barSize,
}: StackedBarChartProps) => {
  const theme = useTheme();

  const [toolTipIndex, setToolTipIndex] = useState(-1);

  const chartData = data.flatMap((entry) => {
    const entrydata: Record<string, any> = {
      title: entry.title,
    };
    entry.data.map(
      (value: any, index: number) =>
        (entrydata[`element_${index}`] = value.value),
    );

    return entrydata;
  });

  const getMinData = () => {
    try {
      return data
        .map((item, index) => ({
          index: index,
          size: item.data.length,
        }))
        .reduce((a, b) => (a.size < b.size ? a : b));
    } catch (e) {
      return { index: -1, size: 0 };
    }
  };

  const minData = getMinData();
  const stackSize = minData.index >= 0 ? data[minData.index].data.length : 0;

  // Recharts rounds every segment when `radius` is set on a Bar, so the outer
  // segment is resolved per bar instead — the last one carrying a value, since
  // trailing statuses are commonly zero and would round nothing visible.
  const getOuterSegmentIndex = (row?: StackRow) => {
    for (let index = stackSize - 1; index >= 0; index--) {
      if (Number(row?.[`element_${index}`] ?? 0) > 0) return index;
    }
    return -1;
  };

  // Solid value lines only — the vertical lines would cut through the bars.
  const gridElement = showGrid ? (
    <CartesianGrid vertical={false} stroke={theme.palette.neutral["010"]} />
  ) : null;

  const tooltipElement = (
    <Tooltip
      content={customTooltip || <SegmentTooltip index={toolTipIndex} />}
      cursor={false}
      // The callout places itself — see ChartTooltip.
      allowEscapeViewBox={customTooltip ? undefined : { x: true }}
    />
  );

  const getSegmentShape = (index: number) => {
    if (!barRadius) return undefined;

    return (shapeProps: RectangleProps & { payload?: StackRow }) => (
      <Rectangle
        {...shapeProps}
        radius={
          getOuterSegmentIndex(shapeProps?.payload) === index
            ? layout === "vertical"
              ? [0, barRadius, barRadius, 0]
              : [barRadius, barRadius, 0, 0]
            : 0
        }
      />
    );
  };

  return isLoading ? (
    <Stack
      sx={{
        justifyContent: "center",
        alignItems: "center",
        // Replace with your desired box background color
        width: "100%",
        height: "100%",
        gap: "8px",
      }}
    >
      <Stack
        flex={1}
        direction={"row"}
        width={"100%"}
        columnGap={"16px"}
        justifyContent={"baseline"}
        alignItems={"end"}
      >
        <Skeleton
          sx={{
            flex: 1,
            height: "100%",
            WebkitTransform: "unset",
            clipPath:
              " polygon(52% 57%, 77% 78%, 100% 34%, 100% 100%, 0 100%, 0 0, 26% 94%)",
          }}
        />
      </Stack>

      <Stack
        direction={"row"}
        height={"32px"}
        width={"100%"}
        overflow={"clip"}
        gap={"16px"}
      >
        {[...Array(7)].map((_, index) => (
          <Skeleton key={index} height={"100%"} width={"80%"} />
        ))}
      </Stack>
    </Stack>
  ) : (
    <>
      {layout === "horizontal" ? (
        <>
          <ResponsiveContainer width={width} height={height}>
            <BarChart
              width={500}
              height={300}
              data={chartData}
              barSize={barSize}
              maxBarSize={40}
              margin={{
                top: 20,
                right: 20,
                left: 10,
                bottom: 5,
              }}
            >
              {gridElement}
              <YAxis
                width={40}
                padding={{ bottom: 0 }}
                tickCount={4}
                tickLine={false}
                axisLine={showAxisLines}
                //   dataKey={valueKey}
                tick={<TickLabel isXaxis={false} />}
                allowDecimals={false}
                stroke={theme.palette.neutral["010"]}
              />
              <XAxis
                dataKey={labelKey}
                tickLine={false}
                axisLine={showAxisLines}
                fontSize={theme.typography["body-small"].fontSize}
                fontWeight={theme.typography["body-small"].fontWeight}
                fontFamily={theme.typography["body-small"].fontFamily}
                color="black"
                allowDataOverflow={false}
                tick={<TickLabel isXaxis={true} />}
                stroke={theme.palette.neutral["010"]}
              />

              {tooltipElement}

              {minData.index >= 0 &&
                data[minData.index].data.map((entry: any, index: number) => {
                  // console.log("🚀 ~ {chartData.map ~ entry:", entry);
                  return (
                    <Bar
                      key={index}
                      dataKey={`element_${index}`}
                      stackId={"stack_bar_id"}
                      //   data={entry}
                      name={entry?.label}
                      fill={entry?.color}
                      shape={getSegmentShape(index)}
                      onMouseLeave={() => setToolTipIndex(index)}
                      onMouseOver={() => setToolTipIndex(index)}
                      cursor={onItemClick ? "pointer" : "default"}
                      onClick={(e) => {
                        onItemClick?.(entry?.[valueKey], entry, e?.title);
                      }}
                    />
                  );
                })}
              {showLegends && (
                <Legend
                  content={LegendBuilder({
                    data: data[minData.index].data,
                    labelKey: "label",
                    color: barChartColor,
                  })}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </>
      ) : (
        <ResponsiveContainer width={width} height={height || "100%"}>
          <BarChart
            width={500}
            height={300}
            data={chartData}
            barSize={barSize}
            layout={layout}
            barCategoryGap={layout !== "vertical" ? "20%" : "10%"}
            margin={{
              top: 20,
              right: 20,
              left: 10,
              bottom: 5,
            }}
          >
            {gridElement}
            <YAxis
              width={layout !== "vertical" ? 40 : 120}
              padding={{ bottom: 0, top: 0 }}
              tickLine={false}
              axisLine={showAxisLines}
              interval={0} // Show all ticks
              allowDecimals={false}
              dataKey={layout !== "vertical" ? undefined : labelKey}
              type={layout !== "vertical" ? "number" : "category"}
              tick={<TickLabel isXaxis={false} />}
              stroke={theme.palette.neutral["010"]}
              // label={{ value: "Time", angle: -90, position: "" }}
            />
            <XAxis
              dataKey={layout !== "vertical" ? labelKey : undefined}
              type={layout !== "vertical" ? "category" : "number"}
              tickLine={false}
              axisLine={showAxisLines}
              interval={0} // Show all ticks
              fontSize={theme.typography["body-small"].fontSize}
              fontWeight={theme.typography["body-small"].fontWeight}
              fontFamily={theme.typography["body-small"].fontFamily}
              color="black"
              allowDataOverflow={false}
              tick={<TickLabel isXaxis={true} />}
              stroke={theme.palette.neutral["010"]}
              // label={{
              //   value: "Index",
              //   position: "",
              //   offset: 10,
              // }}
            />

            {tooltipElement}

            {minData.index >= 0 &&
              data[minData.index].data.map((entry: any, index: number) => {
                return (
                  <Bar
                    key={index}
                    dataKey={`element_${index}`}
                    stackId={"stack_bar_id"}
                    name={entry?.label}
                    fill={entry?.color}
                    shape={getSegmentShape(index)}
                    onMouseLeave={() => setToolTipIndex(index)}
                    onMouseOver={() => setToolTipIndex(index)}
                    cursor={onItemClick ? "pointer" : "default"}
                    onClick={(e) => {
                      onItemClick?.(entry?.[valueKey], entry, e?.title);
                    }}
                  />
                );
              })}
            {showLegends && (
              <Legend
                content={LegendBuilder({
                  data: data[minData.index].data,
                  labelKey: "label",
                  color: barChartColor,
                })}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      )}
    </>
  );
};

interface SegmentTooltipProps {
  active?: boolean;
  payload?: Array<{ name?: string; value?: number }>;
  coordinate?: { x?: number; y?: number };
  viewBox?: { x?: number; width?: number };
  /** Index of the stack segment under the cursor, tracked by the chart. */
  index: number;
}

/**
 * Names one segment of a stack rather than the whole bar — which segment comes
 * from the chart, since a stack hands every one of them to the tooltip.
 */
const SegmentTooltip = ({
  active,
  payload,
  coordinate,
  viewBox,
  index,
}: SegmentTooltipProps) => {
  const segment = index >= 0 ? payload?.[index] : undefined;

  return (
    <ChartTooltip
      active={active && !!segment}
      label={segment?.name}
      value={segment?.value}
      coordinate={coordinate}
      viewBox={viewBox}
    />
  );
};
